import {
    useEffect,
    useId,
    useMemo,
    useState,
    type ReactNode,
} from 'react';
import type { MediaFolderResponse } from '@/core/generated/api';
import classes from './MediaLibrary.module.scss';

interface MediaFolderTreeProps {
    folders: MediaFolderResponse[];
    selected?: string;
    onSelect: (id?: string) => void;
    disabled?: boolean;
}

const FOLDER_BATCH_SIZE = 50;

/** Показывает раскрываемые ветки без отрисовки всего каталога */
export const MediaFolderTree = (
    { folders, selected, onSelect, disabled }: MediaFolderTreeProps
) => {
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(() => new Set());
    const [limits, setLimits] = useState<Map<string | undefined, number>>(() => new Map());
    const treeId = useId();

    const index = useMemo(() => {
        const byId = new Map<string, MediaFolderResponse>();
        const children = new Map<string | undefined, MediaFolderResponse[]>();

        for (const folder of folders) {
            if (!folder.id) continue;
            byId.set(folder.id, folder);
            const parent = folder.parentId ?? undefined;
            const siblings = children.get(parent) ?? [];
            siblings.push(folder);
            children.set(parent, siblings);
        }

        return {
            byId,
            children
        };
    }, [folders]);

    const selectedPath = useMemo(() => {
        const path = new Map<string | undefined, string>();
        const visited = new Set<string>();
        let folder = selected
            ? index.byId.get(selected)
            : undefined;

        while (folder?.id && !visited.has(folder.id)) {
            visited.add(folder.id);
            path.set(folder.parentId ?? undefined, folder.id);
            folder = folder.parentId
                ? index.byId.get(folder.parentId)
                : undefined;
        }

        return path;
    }, [index, selected]);

    useEffect(() => {
        const ancestors = [...selectedPath.values()].filter(id => id !== selected);

        setExpandedFolders(previous => ancestors.every(id => previous.has(id))
            ? previous
            : new Set([...previous, ...ancestors]));
    }, [selected, selectedPath]);

    /** Переключает видимость подпапок, сохраняя выбранную папку */
    const toggle = (id: string): void => setExpandedFolders(previous => {
        const next = new Set(previous);

        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
    });

    /** Показывает следующую порцию папок ветки */
    const showMore = (parent?: string): void => setLimits(previous => {
        const next = new Map(previous);
        next.set(parent, (next.get(parent) ?? FOLDER_BATCH_SIZE) + FOLDER_BATCH_SIZE);

        return next;
    });

    /** Строит только видимую часть дерева с ограничением глубины */
    const branch = (parent?: string, depth = 0): ReactNode => {
        if (depth > 30) return null;
        const children = index.children.get(parent) ?? [];
        const limit = limits.get(parent) ?? FOLDER_BATCH_SIZE;
        const visible = children.slice(0, limit);
        const active = index.byId.get(selectedPath.get(parent) ?? '');

        if (active && !visible.includes(active)) visible.push(active);
        const remaining = children.length - visible.length;

        return <>
            {visible.map(folder => {
                const id = folder.id;

                if (!id) return null;
                const hasChildren = index.children.has(id);
                const expanded = expandedFolders.has(id);
                const childrenId = `${treeId}-${id}`;

                return <div key={id}>
                    <div
                        className={classes.folderRow}
                        style={{ paddingLeft: `${depth * 18}px` }}
                    >
                        {hasChildren
                            ? <button
                                type="button"
                                className={classes.folderToggle}
                                disabled={disabled}
                                aria-label={`${expanded
                                    ? 'Свернуть'
                                    : 'Раскрыть'} папку ${folder.name}`}
                                aria-expanded={expanded}
                                aria-controls={childrenId}
                                onClick={() => toggle(id)}
                            >
                                <i
                                    aria-hidden="true"
                                    className={expanded
                                        ? 'pi pi-angle-down'
                                        : 'pi pi-angle-right'
                                    }
                                />
                            </button>
                            : <span
                                className={classes.folderSpacer}
                                aria-hidden="true"
                            />}
                        <button
                            type="button"
                            disabled={disabled}
                            aria-current={selected === id
                                ? 'page'
                                : undefined
                            }
                            className={selected === id
                                ? classes.activeFolder
                                : classes.folderNode
                            }
                            onClick={() => onSelect(id)}
                        >
                            <i
                                aria-hidden="true"
                                className="pi pi-folder"
                            /> <span>{folder.name}</span>
                        </button>
                    </div>
                    {hasChildren && expanded &&
                        <div id={childrenId}>{branch(id, depth + 1)}</div>
                    }
                </div>;
            })}
            {remaining > 0 &&
                <button
                    type="button"
                    className={classes.folderNode}
                    disabled={disabled}
                    aria-label={`Показать ещё папки: ${index.byId.get(parent ?? '')?.name ?? 'корень'}`}
                    onClick={() => showMore(parent)}
                >
                    Показать ещё ({remaining})
                </button>
            }
        </>;
    };

    return <nav
        className={classes.tree}
        aria-label="Папки медиатеки"
    >
        <button
            type="button"
            disabled={disabled}
            aria-current={!selected
                ? 'page'
                : undefined
            }
            className={!selected
                ? classes.activeFolder
                : classes.folderNode
            }
            onClick={() => onSelect(undefined)}
        >
            <i
                aria-hidden="true"
                className="pi pi-folder"
            /> Все файлы
        </button>
        {branch()}
    </nav>;
};
